import { useState, useRef, useEffect, ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { ff } from "@/lib/feature-flags";
import { Skeleton } from "./Skeleton";

interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Explicit width/height avoids CLS */
  width?: number;
  height?: number;
  /** Skeleton placeholder while loading */
  skeletonClassName?: string;
  /** data-testid for E2E */
  testId?: string;
}

/**
 * Lazy-loaded image with IntersectionObserver.
 *
 * - Shows a skeleton placeholder until the image is in the viewport
 * - Uses native loading="lazy" as fallback
 * - Fades in on load for smooth UX
 * - Falls back to eager loading when feature flag is off
 */
export function LazyImage({
  src,
  alt,
  className,
  skeletonClassName,
  testId,
  width,
  height,
  ...rest
}: LazyImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isInView, setIsInView] = useState(!ff.imageLazyLoading);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!ff.imageLazyLoading) return;

    const el = imgRef.current;
    if (!el) return;

    // IntersectionObserver with generous rootMargin for preloading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px" } // Start loading 200px before viewport
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const showSkeleton = ff.imageLazyLoading && (!isInView || !isLoaded) && !hasError;

  return (
    <div
      ref={imgRef}
      className={cn("relative overflow-hidden", skeletonClassName)}
      style={width && height ? { aspectRatio: `${width}/${height}` } : undefined}
      data-testid={testId}
    >
      {showSkeleton && (
        <Skeleton className="absolute inset-0 h-full w-full" />
      )}

      {isInView && (
        <img
          src={src}
          alt={alt ?? ""}
          width={width}
          height={height}
          loading={ff.imageLazyLoading ? "lazy" : "eager"}
          decoding="async"
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={cn(
            "transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
            className
          )}
          {...rest}
        />
      )}

      {hasError && (
        <div className="flex h-full w-full items-center justify-center bg-amber-50 text-amber-400">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
          </svg>
        </div>
      )}
    </div>
  );
}
